// src/pages/ConviteFazenda/ConviteFazenda.js
// Tela de aceite do convite nominal (PG-042). Rota PRIVADA — é o PrivateRoute
// que gera o `state.from` de que o LoginGigya depende para devolver o usuário
// a esta rota, com o mesmo `?t=`, depois de autenticar.
//
// O aceite NÃO escreve no WatermelonDB. Quem traz o vínculo novo é o pull do
// sync que já existe — e ele precisa ser explícito (G018: o loop de
// background é push-only por default).
//
// Segurança (RV-1): o `?t=` desta rota carrega o token assinado do convite.
// Todo `recordEvent`/`recordError` disparado DAQUI passa `url` explícito sem
// o token — mas o `pullFarms` (dentro de `src/syncWorker.js`, zona
// STABILITY-CRITICAL, fora do nosso controle) dispara os próprios eventos
// internos sem isso, carimbando `window.location.search` da hora em que
// rodam. Por isso, assim que o aceite responde 200, ANTES de qualquer novo
// side-effect (a começar pelo pull), o `?t=` é removido da URL via
// `history.replaceState` — o convite já foi aceito, o token já cumpriu a
// função, e os eventos internos do syncWorker que rodam depois disso
// carimbam uma URL limpa. Só depois do 200: nos caminhos de erro/sem-resposta
// o token permanece, para o usuário poder recarregar e tentar de novo.

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link as RouterLink, useLocation } from 'react-router-dom'

import { Alert, Box, CircularProgress, Typography } from '@mui/material'

import * as sync from '../../syncWorker'
import AppContent from '../../components/layout/AppContent'
import TitleBar from '../../components/TitleBar'
import TopBar from '../../components/TopBar'
import { AVAILABLE_ENTITIES, utils } from '../../database'
import { recordError, recordEvent } from '../../observability/events'
import {
  ESTADO_ACEITE,
  MENSAGEM_LINK_INCOMPLETO,
  MENSAGEM_SEM_RESPOSTA,
  ROTA_CONVITE,
  aceitarConvite,
  deveAceitar,
  deveDispararPull,
  estadoDaTelaDeAceite,
  extrairTokenDaBusca,
  houveErroDeBackend,
} from './conviteFazendaLogica'

export default function ConviteFazenda() {
  const location = useLocation()
  const dispatch = useDispatch()

  const token = useMemo(
    () => extrairTokenDaBusca(location.search),
    [location.search],
  )

  // `resposta` segue o mesmo vocabulário de `estadoDaTelaDeAceite`:
  // `undefined` = ainda em voo, `null` = backend não respondeu, objeto = veio.
  const [resposta, setResposta] = useState(undefined)
  const [fazendaLocal, setFazendaLocal] = useState(null)
  const [pullFalhou, setPullFalhou] = useState(false)
  const [sincronizando, setSincronizando] = useState(false)

  // Sem esta trava o StrictMode do React 19 dispararia o aceite duas vezes em
  // desenvolvimento. O endpoint é idempotente, mas uma requisição a mais não
  // deixa de ser uma requisição a mais.
  const tokenJaAceito = useRef(null)

  useEffect(() => {
    // Sem token não existe requisição a fazer: nada é disparado, e o que a
    // tela diz é texto próprio do front — o backend nunca foi consultado.
    if (!deveAceitar(token)) return undefined
    if (tokenJaAceito.current === token) return undefined
    tokenJaAceito.current = token

    let cancelado = false

    const aceitar = async () => {
      let resultado
      try {
        resultado = await aceitarConvite(dispatch, token)
      } catch (e) {
        recordError(e instanceof Error ? e : new Error(String(e)), {
          category: 'farm',
          action: 'farm_invite_accept_failed',
          // Explícito: sem isto, events.ts carimba pathname+search da URL
          // atual, e esta rota carrega o token assinado do convite em `?t=`.
          payload: { url: ROTA_CONVITE },
        })
        if (!cancelado) setResposta(null)
        return
      }

      if (cancelado) return

      // `api.post` devolve `null` quando a requisição nem chegou a ter
      // resposta (offline, rede caída) — `estadoDaTelaDeAceite` trata isso
      // como 'sem-resposta', distinto de recusa do convite.
      setResposta(resultado === undefined ? null : resultado)

      if (!resultado) return
      if (houveErroDeBackend(resultado)) return

      recordEvent({
        level: 'info',
        category: 'farm',
        action: 'farm_invite_accepted',
        payload: { farmId: resultado.fazendaId, url: ROTA_CONVITE },
      })

      // O aceite JÁ teve sucesso — o token cumpriu a função. Tira o `?t=` da
      // URL antes de qualquer novo side-effect (a começar pelo pull) para
      // que os eventos internos do syncWorker (fora do nosso controle)
      // parem de ver o token em `window.location.search`.
      //
      // `history.replaceState`, não `navigate()`: muda a barra de endereço
      // sem passar pelo router — `location.search` deste componente não
      // muda, o `useMemo` de `token` não recalcula, e a dependência do
      // efeito não dispara a cleanup (`cancelado = true`) no meio do
      // caminho, o que quebraria os `setState` daqui para baixo (pull
      // falhou, nome da fazenda, `sincronizando`). É `replace`, não `push`:
      // não suja o histórico — um "voltar" não reabre a tela de convite.
      if (typeof window !== 'undefined' && window.history?.replaceState) {
        window.history.replaceState(null, '', ROTA_CONVITE)
      }

      if (!deveDispararPull(resultado)) return

      // O convite JÁ foi aceito no servidor. Daqui para baixo nada pode
      // transformar a tela em erro de convite: uma falha de pull é falha de
      // sincronização, não recusa do convite.
      setSincronizando(true)
      try {
        // Pull DIRECIONADO à fazenda recém-vinculada — precedente:
        // src/pages/Home.js:723. Não `forceSync`: se já houver um sync em
        // curso, `forceSync` degrada para push-only e o pull que este
        // caminho exige nunca acontece; e `forceSync` engole a própria
        // exceção internamente, o que deixaria o catch abaixo sem sinal
        // nenhum para reagir.
        await sync.pullFarms([resultado.fazendaId], {}, false, true, true)
      } catch (e) {
        if (!cancelado) {
          setPullFalhou(true)
          recordEvent({
            level: 'warn',
            category: 'sync',
            action: 'farm_invite_pull_failed',
            payload: {
              farmId: resultado.fazendaId,
              message: e?.message,
              url: ROTA_CONVITE,
            },
          })
        }
      }

      try {
        const fazenda = await utils.getElement(
          AVAILABLE_ENTITIES.FARMS,
          resultado.fazendaId,
        )
        if (!cancelado && fazenda) setFazendaLocal(fazenda)
      } catch (e) {
        // Fazenda local indisponível não é erro de convite — o link por id
        // continua funcionando; estadoDaTelaDeAceite degrada graciosamente.
      }

      if (!cancelado) setSincronizando(false)
    }

    aceitar()

    return () => {
      cancelado = true
    }
  }, [token, dispatch])

  const estado = estadoDaTelaDeAceite({
    token,
    resposta,
    fazendaLocal,
    pullFalhou,
  })

  return (
    <AppContent>
      <TopBar title='' />
      <TitleBar
        title='Convite de fazenda'
        paths={[{ label: 'Convite de fazenda' }]}
      />

      {estado.tipo === ESTADO_ACEITE.LINK_INCOMPLETO && (
        <Alert severity='warning'>{MENSAGEM_LINK_INCOMPLETO}</Alert>
      )}

      {estado.tipo === ESTADO_ACEITE.CARREGANDO && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}
          data-testid='convite-carregando'
        >
          <CircularProgress size={24} />
          <Typography variant='body1'>Validando seu convite...</Typography>
        </Box>
      )}

      {estado.tipo === ESTADO_ACEITE.ERRO && (
        <Alert severity='error' data-testid='convite-erro'>
          {estado.mensagem}
        </Alert>
      )}

      {estado.tipo === ESTADO_ACEITE.SEM_RESPOSTA && (
        <Alert severity='warning' data-testid='convite-sem-resposta'>
          {MENSAGEM_SEM_RESPOSTA}
        </Alert>
      )}

      {estado.tipo === ESTADO_ACEITE.SUCESSO && (
        <Box data-testid='convite-sucesso'>
          <Alert severity='success' sx={{ mb: 2 }}>
            Convite aceito. Você agora é responsável por esta fazenda.
          </Alert>

          {estado.nomeDaFazenda && (
            <Typography variant='h6' sx={{ mb: 1 }}>
              {estado.nomeDaFazenda}
            </Typography>
          )}

          {sincronizando && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size={16} />
              <Typography variant='body2'>Atualizando seus dados...</Typography>
            </Box>
          )}

          {/* O link só existe quando o aceite devolveu o fazendaId — mesmo
              princípio do controle de copiar do modal de geração: nenhum
              controle sem o dado. */}
          {estado.href && (
            <Typography variant='body1'>
              <RouterLink to={estado.href}>Ir para a fazenda</RouterLink>
            </Typography>
          )}
        </Box>
      )}
    </AppContent>
  )
}
